import React, { useState, useEffect, JSX } from "react";
import { useNavigate } from "react-router-dom";
import {
    StatCard,
    StatCardHeader,
    StatCardTitle,
    StatCardIcon,
    StatCardContent,
    StatCardValue,
} from "../../components/ui/stat-card";
import { Truck, CheckCircle, Clock, Target } from "lucide-react";
import {
    startOfDay,
    endOfDay,
    subDays,
    startOfMonth,
    endOfMonth,
    subMonths,
    startOfYear,
} from "date-fns";
import { ShipmentChart, ShipmentSeries } from "./components/ShipmentChart";
import { getShipmentStats, getShipmentSeries, ShipmentStats, Period, LiveStat } from "./api/stats";
import { useBreadcrumbs } from "app/breadcrumb-provider";
import { Alert, AlertDescription } from "components/ui/alert";


const Dashboard: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();
    const navigate = useNavigate();

    const [error, setError] = useState<string>("");
    const [selectedPeriod, setSelectedPeriod] = useState<Period>("last7days");
    const [stats, setStats] = useState<ShipmentStats>({total_shipments: 0, delivered: 0, in_transit: 0});
    const [seriesData, setSeriesData] = useState<ShipmentSeries[] | any>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [chartLoading, setChartLoading] = useState<boolean>(true);

    const getIcon = (iconName: LiveStat["icon"]): JSX.Element | null => {
        const icons = {
            truck: Truck,
            check: CheckCircle,
            clock: Clock,
            target: Target,
        };

        const IconComponent = icons[iconName];
        return IconComponent ? <IconComponent className="h-4 w-4" /> : null;
    };

    const toISTISOString = (date: Date): string => {
        const istOffset = 5.5 * 60 * 60 * 1000;
        return new Date(date.getTime() + istOffset).toISOString();
    };

    const getDateRange = (period: Period): { from: string; to: string } => {
        const now = new Date();
        let from: Date;
        let to: Date;

        switch (period) {
            case "today":
                from = startOfDay(now);
                to = endOfDay(now);
                break;
            case "last7days":
                from = startOfDay(subDays(now, 6));
                to = endOfDay(now);
                break;
            case "last30days":
                from = startOfDay(subDays(now, 29));
                to = endOfDay(now);
                break;
            case "thisMonth":
                from = startOfMonth(now);
                to = endOfDay(now);
                break;
            case "lastMonth": {
                const lastMonth = subMonths(now, 1);
                from = startOfMonth(lastMonth);
                to = endOfMonth(lastMonth);
                break;
            }
            case "ytd":
                from = startOfYear(now);
                to = endOfDay(now);
                break;
            default:
                from = startOfDay(subDays(now, 6));
                to = endOfDay(now);
        }
        return {
            from: toISTISOString(from),
            to: toISTISOString(to),
        };
    };


    const fetchStats = async (period: Period): Promise<void> => {
        setLoading(true);
        setError("");
        try {
            const { from, to } = getDateRange(period);
            const data: ShipmentStats = await getShipmentStats(from, to);
            setStats(data);
        } catch (err: any) {
            setError(err?.response?.data?.detail || err?.message || "Failed to fetch dashboard stats");
        } finally {
            setLoading(false);
        }
    };

    const fetchSeriesData = async (period: Period): Promise<void> => {
        setChartLoading(true);
        try {
            const { from, to } = getDateRange(period);
            const data = await getShipmentSeries(from, to);
            setSeriesData(data.series ?? []);
        } catch (error) {
            console.error("Error fetching shipment series:", error);
            setSeriesData([]);
        } finally {
            setChartLoading(false);
        }
    };

    useEffect(() => {
        setBreadcrumbs([
            { label: "Dashboard", href: "/dashboard" }
        ]);
    }, [setBreadcrumbs]);

    useEffect(() => {
        Promise.all([
            fetchStats(selectedPeriod),
            fetchSeriesData(selectedPeriod),
        ]);
    }, [selectedPeriod]);


    const dateButtons: { key: Period; label: string }[] = [
        { key: "today", label: "Today" },
        { key: "last7days", label: "Last 7 days" },
        { key: "last30days", label: "Last 30 days" },
        { key: "thisMonth", label: "This month" },
        { key: "lastMonth", label: "Last month" },
        { key: "ytd", label: "YTD" },
    ];

    const liveStats: LiveStat[] = [
        {
            id: "total",
            title: "Total Shipments",
            value: stats.total_shipments,
            variant: "total",
            icon: "truck",
        },
        {
            id: "delivered",
            title: "Delivered",
            value: stats.delivered,
            variant: "positive",
            icon: "check",
        },
        {
            id: "transit",
            title: "In Transit",
            value: stats.in_transit,
            variant: "total",
            icon: "clock",
        },
    ];

    return (
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            <div className="flex flex-wrap gap-2">
                {dateButtons.map((button) => (
                    <button
                        key={button.key}
                        disabled={loading}
                        onClick={() => !loading && setSelectedPeriod(button.key)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${selectedPeriod === button.key
                                ? "bg-blue-100 text-blue-700 border border-blue-200"
                                : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-300"
                            } ${loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                    >
                        {button.label}
                    </button>
                ))}
            </div>

            {error && (
                <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-3">
                {liveStats.map((stat) => (
                    <StatCard
                        key={stat.id}
                        variant={stat.variant}
                        onClick={
                            stat.id === "delivered"
                                ? () => navigate("/shipments/delivered")
                                : stat.id === "transit"
                                    ? () => navigate("/shipments/in-transit")
                                    : undefined
                        }
                    >
                        <StatCardHeader>
                            <StatCardTitle>{stat.title}</StatCardTitle>
                            <StatCardIcon>{getIcon(stat.icon)}</StatCardIcon>
                        </StatCardHeader>
                        <StatCardContent>
                            <StatCardValue>
                                {loading ? "..." : stat.value.toLocaleString()}
                            </StatCardValue>
                        </StatCardContent>
                    </StatCard>
                ))}
            </div>

            <ShipmentChart seriesData={seriesData} loading={chartLoading} />
        </div>
    );
};

export default Dashboard;
